import { api } from "./api";
import { BookingStatus, PaymentStatus } from "./bookings";
import { ProviderStatus, UserRole } from "@/contexts/AuthContext";
import { TourCategory } from "./tours";
import type { ProviderDocument } from "./providerDocuments";

export const TourStatus = { Active: 0, Paused: 1, Archived: 2 } as const;
export type TourStatus = typeof TourStatus[keyof typeof TourStatus];

export type AdminProvider = {
  id: string;
  companyName: string;
  ownerFullName: string;
  ownerEmail: string;
  rfc: string | null;
  location: string | null;
  description: string | null;
  website: string | null;
  contactPhone: string | null;
  commissionRate: number;
  verified: boolean;
  status: ProviderStatus;
  tourCount: number;
  bookingCount: number;
  lifetimeGross: number;
  createdAt: string;
};

export type AdminBooking = {
  id: string;
  reference: string;
  status: BookingStatus;
  date: string;
  startTime: string | null;
  adults: number;
  children: number;
  totalPrice: number;
  commissionAmount: number;
  currency: string;
  customerName: string;
  customerEmail: string;
  tourId: string;
  tourTitle: string;
  providerId: string;
  providerName: string;
  paymentStatus: PaymentStatus | null;
  createdAt: string;
};

export type DashboardMonth = {
  yearMonth: string;
  label: string;
  gross: number;
  commission: number;
  bookings: number;
};

export type AdminDashboardStats = {
  totalGrossLifetime: number;
  totalCommissionLifetime: number;
  bookingsLifetime: number;
  activeProviders: number;
  pendingProviders: number;
  activeTours: number;
  totalUsers: number;
  bookings30d: number;
  gross30d: number;
  commission30d: number;
  currency: string;
  monthly: DashboardMonth[];
};

export function getAdminStats(): Promise<AdminDashboardStats> {
  return api<AdminDashboardStats>("/admin/dashboard-stats");
}

export function listAdminProviders(params: { status?: ProviderStatus; q?: string } = {}): Promise<AdminProvider[]> {
  const qs = new URLSearchParams();
  if (params.status !== undefined) qs.set("status", String(params.status));
  if (params.q) qs.set("q", params.q);
  const suffix = qs.toString();
  return api<AdminProvider[]>(`/admin/providers${suffix ? `?${suffix}` : ""}`);
}

export function verifyProvider(id: string): Promise<AdminProvider> {
  return api<AdminProvider>(`/admin/providers/${id}/verify`, { method: "POST" });
}

export function suspendProvider(id: string, reason?: string): Promise<AdminProvider> {
  return api<AdminProvider>(`/admin/providers/${id}/suspend`, { method: "POST", body: { reason } });
}

export type UpdateAdminProviderInput = {
  companyName: string;
  rfc?: string | null;
  location?: string | null;
  description?: string | null;
  website?: string | null;
  contactPhone?: string | null;
};

/** Edits the provider's own details. The API overwrites every field it receives, so
 *  always send the full set — a missing key blanks that column. */
export function updateAdminProvider(id: string, input: UpdateAdminProviderInput): Promise<AdminProvider> {
  return api<AdminProvider>(`/admin/providers/${id}`, { method: "PUT", body: input });
}

export function listAdminBookings(params: {
  status?: BookingStatus;
  providerId?: string;
  from?: string;
  to?: string;
} = {}): Promise<AdminBooking[]> {
  const qs = new URLSearchParams();
  if (params.status !== undefined) qs.set("status", String(params.status));
  if (params.providerId) qs.set("providerId", params.providerId);
  if (params.from) qs.set("from", params.from);
  if (params.to) qs.set("to", params.to);
  const suffix = qs.toString();
  return api<AdminBooking[]>(`/admin/bookings${suffix ? `?${suffix}` : ""}`);
}

export type AuditLog = {
  id: string;
  actorUserId: string | null;
  actorEmail: string | null;
  method: string;
  path: string;
  routeValues: string | null;
  requestBody: string | null;
  statusCode: number;
  ip: string | null;
  at: string;
};

export function listAuditLogs(params: { search?: string; limit?: number } = {}): Promise<AuditLog[]> {
  const qs = new URLSearchParams();
  if (params.search) qs.set("search", params.search);
  if (params.limit !== undefined) qs.set("limit", String(params.limit));
  const suffix = qs.toString();
  return api<AuditLog[]>(`/admin/audit${suffix ? `?${suffix}` : ""}`);
}

export const PayoutStatus = { Pending: 0, Settled: 1 } as const;
export type PayoutStatus = typeof PayoutStatus[keyof typeof PayoutStatus];

export type PayoutSummary = {
  providerId: string;
  providerName: string;
  pendingNet: number;
  pendingCount: number;
  settledNet: number;
  settledCount: number;
  currency: string;
};

export type PayoutEntry = {
  id: string;
  providerId: string;
  providerName: string;
  bookingId: string;
  bookingReference: string;
  grossAmount: number;
  commissionAmount: number;
  netPayable: number;
  currency: string;
  status: PayoutStatus;
  settlementReference: string | null;
  settledAt: string | null;
  createdAt: string;
};

export function getPayoutSummary(): Promise<PayoutSummary[]> {
  return api<PayoutSummary[]>("/admin/payouts/summary");
}

export function listPayoutEntries(params: { providerId?: string; status?: PayoutStatus } = {}): Promise<PayoutEntry[]> {
  const qs = new URLSearchParams();
  if (params.providerId) qs.set("providerId", params.providerId);
  if (params.status !== undefined) qs.set("status", String(params.status));
  const suffix = qs.toString();
  return api<PayoutEntry[]>(`/admin/payouts${suffix ? `?${suffix}` : ""}`);
}

export function settlePayouts(entryIds: string[], reference: string): Promise<{ settled: number }> {
  return api<{ settled: number }>("/admin/payouts/settle", { method: "POST", body: { entryIds, reference } });
}

export type AdminProduct = {
  id: string;
  slug: string;
  title: string;
  category: TourCategory;
  location: string;
  priceAdult: number;
  status: TourStatus;
  isFeatured: boolean;
  rating: number;
  reviewCount: number;
  providerId: string;
  providerName: string;
  createdAt: string;
};

export function setProductFeatured(id: string, featured: boolean): Promise<{ isFeatured: boolean }> {
  return api<{ isFeatured: boolean }>(`/admin/products/${id}/feature`, { method: "POST", body: { featured } });
}

export function listAdminProducts(params: { status?: TourStatus; search?: string } = {}): Promise<AdminProduct[]> {
  const qs = new URLSearchParams();
  if (params.status !== undefined) qs.set("status", String(params.status));
  if (params.search) qs.set("search", params.search);
  const suffix = qs.toString();
  return api<AdminProduct[]>(`/admin/products${suffix ? `?${suffix}` : ""}`);
}

export type AdminUser = {
  id: string;
  email: string;
  fullName: string;
  role: UserRole;
  emailVerified: boolean;
  acceptsMarketing: boolean;
  bookingsCount: number;
  createdAt: string;
};

export function listAdminUsers(params: { role?: UserRole; search?: string } = {}): Promise<AdminUser[]> {
  const qs = new URLSearchParams();
  if (params.role !== undefined) qs.set("role", String(params.role));
  if (params.search) qs.set("search", params.search);
  const suffix = qs.toString();
  return api<AdminUser[]>(`/admin/users${suffix ? `?${suffix}` : ""}`);
}

const docApiOrigin = ((import.meta.env.VITE_API_BASE_URL as string | undefined)?.replace(/\/$/, "")
  ?? "http://localhost:5080/api").replace(/\/api$/, "");
function absDocUrl(u: string): string {
  return u.startsWith("http") ? u : `${docApiOrigin}${u}`;
}

export async function listProviderDocumentsAdmin(providerId: string): Promise<ProviderDocument[]> {
  const docs = await api<ProviderDocument[]>(`/admin/providers/${providerId}/documents`);
  return docs.map((d) => ({ ...d, fileUrl: absDocUrl(d.fileUrl) }));
}

export function verifyProviderDocument(id: string): Promise<ProviderDocument> {
  return api<ProviderDocument>(`/admin/documents/${id}/verify`, { method: "POST" });
}

export function rejectProviderDocument(id: string, note?: string): Promise<ProviderDocument> {
  return api<ProviderDocument>(`/admin/documents/${id}/reject`, { method: "POST", body: { note } });
}

export function deleteProviderDocument(id: string): Promise<void> {
  return api<void>(`/admin/documents/${id}`, { method: "DELETE" });
}

export const BroadcastAudience = { Providers: 0, Clients: 1, All: 2 } as const;
export type BroadcastAudience = typeof BroadcastAudience[keyof typeof BroadcastAudience];

export function broadcastNotification(input: { audience: BroadcastAudience; title: string; body: string; linkUrl?: string }): Promise<{ sent: number }> {
  return api<{ sent: number }>("/admin/broadcast", { method: "POST", body: input });
}

export type PlatformSettings = {
  platformName: string;
  supportEmail: string;
  defaultCommissionRate: number; // 0..1
  defaultCurrency: string;
};

export function getPlatformSettings(): Promise<PlatformSettings> {
  return api<PlatformSettings>("/admin/settings");
}

export function updatePlatformSettings(input: PlatformSettings): Promise<PlatformSettings> {
  return api<PlatformSettings>("/admin/settings", { method: "PUT", body: input });
}
